import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { StatCard } from '../components/common/StatCard';
import { SkeletonCard } from '../components/common/SkeletonCard';
import { EmptyState } from '../components/common/EmptyState';
import { Button } from '../components/ui/button';
import { formatScore } from '../utils/helpers';
import { BarChart3, Users, Target, Award, ArrowLeft, Download } from 'lucide-react';

export default function ModuleAnalyticsPage() {
  const { moduleId } = useParams();
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get(`/analytics/module/${moduleId}`).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [get, moduleId]);

  const handleExport = () => {
    const url = `/api/v1/analytics/export/module/${moduleId}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="space-y-4">{Array(4).fill(0).map((_, i) => <SkeletonCard key={`skel-${i}`} />)}</div>;
  if (!data) return <EmptyState icon={BarChart3} title="No analytics data" description="Not enough data yet." />;

  return (
    <div className="space-y-6" data-testid="module-analytics">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/analytics" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
          <h1 className="font-display text-2xl font-bold tracking-tight">{data.module_title || 'Module Analytics'}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Enrolled" value={data.total_enrolled || 0} icon={Users} />
        <StatCard label="Completed" value={data.total_completed || 0} icon={Target} />
        <StatCard label="Pass Rate" value={`${data.pass_rate || 0}%`} icon={Award} />
        <StatCard label="Avg Score" value={formatScore(data.average_score)} icon={BarChart3} />
      </div>

      {/* Score Distribution */}
      {data.score_distribution && (
        <div className="rounded-lg border bg-card p-6">
          <h3 className="font-semibold mb-4">Score Distribution</h3>
          <div className="flex items-end gap-2 h-40">
            {Object.entries(data.score_distribution).map(([range, count]) => {
              const max = Math.max(...Object.values(data.score_distribution), 1);
              const height = (count / max) * 100;
              return (
                <div key={range} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-muted-foreground">{count}</span>
                  <div className="w-full rounded-t bg-[var(--lumyn-content)]" style={{ height: `${Math.max(height, 4)}%` }} />
                  <span className="text-[10px] text-muted-foreground">{range}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Participant List */}
      {data.trainees && data.trainees.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr>
              <th className="text-left p-3 font-medium">Participant</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-left p-3 font-medium">Score</th>
              <th className="text-left p-3 font-medium hidden md:table-cell">Time Spent</th>
            </tr></thead>
            <tbody>
              {data.trainees.map((t) => (
                <tr key={t.user_id || t.email} className="border-t" data-testid="trainee-row">
                  <td className="p-3 font-medium">{t.name || t.email}</td>
                  <td className="p-3"><span className={`text-xs font-medium ${t.status === 'completed' ? 'text-emerald-500' : t.status === 'in_progress' ? 'text-amber-500' : 'text-muted-foreground'}`}>{t.status}</span></td>
                  <td className="p-3">{t.score != null ? formatScore(t.score) : '—'}</td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground">{t.time_spent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
